<template>
  <component
    :is="component"
    :class="['kvass-file__thumbnail', readonly && 'kvass-file__thumbnail--clickable']"
    v-bind="bindings"
    v-on="handlers"
  >
    <div class="kvass-file__thumbnail-icon">
      <slot name="icon">
        <a
          v-if="value.type.includes('image')"
          id="image-thumbnail"
          :href="value.url"
          :data-pswp-width="dimensions.width"
          :data-pswp-height="dimensions.height"
          target="_blank"
        >
          <img
            class="kvass-file__thumbnail-image-preview"
            :src="value.url"
            :alt="value.name"
          />
        </a>

        <Icon v-else :icon="icon" />
      </slot>
    </div>
    <div class="kvass-file__thumbnail-info">
      <input
        type="text"
        ref="input"
        class="kvass-file__thumbnail-name"
        :value="value.name"
        @keypress.enter="unfocus"
        :disabled="readonly || !rename"
        @input="$emit('rename', $event.target.value)"
      />
      <div class="kvass-file__thumbnail-info-footer">
        <span v-if="value.size" class="kvass-file__thumbnail-size">{{
          value.size | size
        }}</span>
      </div>
    </div>

    <slot name="before-action"></slot>

    <ButtonGroup :use-portal="false">
      <ButtonComponent
        v-for="item in actions.filter(i => Boolean(i.previewAction))"
        @click.prevent="item.action"
        :title="item.label"
        v-bind="item"
        label=""
      />
      <template v-if="!readonly" #secondary>
        <ButtonComponent
          v-for="item in actions"
          @click.prevent="item.action"
          :title="item.label"
          v-bind="item"
        />
      </template>
    </ButtonGroup>

    <div v-if="sortable" class="kvass-file__thumbnail-draggable-handle">
      <Icon :icon="['fal', 'arrows-up-down']" />
    </div>

    <slot name="action" />
  </component>
</template>

<script>
import Icon from '@kvass/vue2-icon'
import PhotoSwipeLightbox from 'photoswipe/dist/photoswipe-lightbox.esm.js'
import { ButtonComponent } from 'vue-elder-button'
import ButtonGroup from '@kvass/button-group'

import { BytesToSize, CopyToClipboard } from './utils'
import { Options } from '../index'

const iconPatters = [
  { pattern: /text.*/, icon: 'file-alt' },
  { pattern: /image.*/, icon: 'file-image' },
  { pattern: /pdf/, icon: 'file-pdf' },
  { pattern: /word/, icon: 'file-word' },
  { pattern: /excel/, icon: 'file-excel' },
  { pattern: /powerpoint/, icon: 'file-powerpoint' },
  { pattern: /octet-stream|zip/, icon: 'file-archive' },
]

const excludeDownloadOn = ['kvass-file__thumbnail-image-preview']

export default {
  props: {
    value: Object,
    readonly: Boolean,
    rename: Boolean,
    sortable: Boolean,
    customActions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      clipPromise: null,
    }
  },
  computed: {
    dimensions() {
      const data = this.value.dimensions || []
      return {
        width: data[0],
        height: data[1],
      }
    },
    actions() {
      return [
        {
          label: this.$t('downloadX', { x: '' }),
          action: () => this.maybeDownload(),
          icon: ['fal', 'arrow-down-to-line'],
          previewAction: true,
          tag: 'a',
          href: this.value.url,
        },
        {
          label: this.$t('copyLink'),
          action: this.copy,
          icon: ['fal', 'copy'],
          promise: this.clipPromise,
          'success-label': this.$t('copied'),
          condition: !this.readonly,
        },
        {
          label: this.$t('delete'),
          action: () => this.$emit('delete'),
          icon: ['fal', 'times'],
          condition: !this.readonly,
        },
        ...this.customActions,
      ].filter(f => !('condition' in f) || f.condition)
    },
    icon() {
      let match =
        [...Options.icons, ...iconPatters].find(e => this.value.type.match(e.pattern)) ||
        {}
      return match.icon || 'file'
    },
    component() {
      if (this.readonly) return 'a'
      return 'div'
    },
    handlers() {
      if (!this.readonly) return {}
      return {
        click: event => {
          //prevent download on image
          if (event.target.className.split(' ').some(c => excludeDownloadOn.includes(c)))
            return
          event.preventDefault()
          this.download()
        },
      }
    },
    bindings() {
      if (!this.readonly) return {}
      return {
        title: this.readonly ? 'Download' : null,
      }
    },
  },
  methods: {
    init() {
      this.lightbox = new PhotoSwipeLightbox({
        gallery: '#image-thumbnail',
        pswpModule: () => import('photoswipe/dist/photoswipe.esm.js'),
      })
      this.lightbox.init()
    },
    unfocus() {
      this.$refs.input.blur()
    },
    copy() {
      if (!this.value.url) return
      return (this.clipPromise = Promise.resolve(CopyToClipboard(this.value.url)))
    },
    download() {
      fetch(this.value.url, { headers: { 'Cache-Control': 'no-cache' } })
        .then(res => res.blob())
        .then(blob => {
          const blobURL = URL.createObjectURL(blob)
          return trigger(blobURL, this.value.name)
        })
        .catch(err => {
          return trigger(this.value.url, this.value.name, '_blank')
        })

      let trigger = (href, name, target) => {
        const a = document.createElement('a')
        a.href = href
        a.style = 'display: none'
        if (target) a.target = target
        if (name) a.download = name

        document.body.appendChild(a)
        a.click()

        document.body.removeChild(a)
      }
    },
    maybeDownload() {
      if (this.readonly) return
      this.download()
    },
  },
  filters: {
    size: BytesToSize,
  },
  mounted() {
    this.init()
  },
  unmounted() {
    if (this.lightbox) {
      this.lightbox.destroy()
      this.lightbox = null
    }
  },
  components: {
    Icon,
    ButtonComponent,
    ButtonGroup,
  },
}
</script>

<style lang="scss">
@import './main';

.kvass-file__thumbnail {
  $spacing: 1rem;
  line-height: 1;
  position: relative;
  display: flex;
  align-items: center;
  padding: $spacing;
  border: 1px solid GetVariable('border-color');
  border-radius: GetVariable('border-radius');
  background-color: white;
  color: inherit;

  .kvass-button-group {
    .elder-button .elder-button__icon:first-child:last-child {
      padding: 0.5em 0.75em !important;
    }
  }
  &--clickable {
    cursor: pointer;
  }

  &-icon {
    .svg-inline--fa {
      font-size: 1.5em;
      opacity: 0.5;
    }
  }

  &-draggable-handle {
    padding: 0.5em 0.75em;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: move;
    border-radius: GetVariable('border-radius');
    border: 1px solid GetVariable('border-color');
    &:hover {
      background-color: GetVariable('border-color');
    }
  }

  &-image-preview {
    border: 1px solid GetVariable('border-color');
    border-radius: GetVariable('border-radius');
    padding: 0.3rem;
    height: 4rem;
    width: 4rem;
    object-fit: cover;
  }

  &-name {
    font: inherit;
    font-weight: bold;
    transition: background-color 150ms ease-out;

    color: inherit !important;
    border: none;
    border-radius: GetVariable('border-radius');
    background-color: transparent;

    &:hover:not(:disabled) {
      background-color: GetVariable('border-color');
    }
  }

  &-info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    &-footer {
      display: flex;
      align-items: center;
      margin-top: 0.2rem;
    }
  }

  &-size {
    font-size: 0.8em;
    margin-right: $spacing / 2;
    opacity: 0.75;
  }
}
</style>
